import React, { useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import KanbanLane from "../KanbanLane";

const useStyles = makeStyles(theme => ({
  kanbanWrapper: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflowX: "auto",
    overflowY: "hidden",
    position: "relative",
    height: "100%",
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1),
    boxSizing: "border-box",
    "&::-webkit-scrollbar": {
      height: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.action.disabled,
      borderRadius: "4px",
      "&:hover": {
        backgroundColor: theme.palette.action.active,
      },
    },
  },
  kanbanBoard: {
    display: "flex",
    flexWrap: "nowrap",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    width: "max-content",
    minWidth: "100%",
    boxSizing: "border-box",
  },
}));

/**
 * KanbanBoard - Optimized board component
 * Manages lane rendering with memoization to prevent unnecessary re-renders
 */
const KanbanBoard = React.memo(({
  lanes,
  onCardClick,
  onCardDrop,
}) => {
  const classes = useStyles();

  // Memoize lanes to prevent unnecessary re-renders of lane components
  const memoizedLanes = useMemo(() => {
    return lanes.map((lane) => (
      <KanbanLane
        key={lane.id}
        laneId={lane.id}
        title={lane.title}
        cards={lane.cards}
        backgroundColor={lane.backgroundColor}
        onCardClick={onCardClick}
        onCardDrop={onCardDrop}
      />
    ));
  }, [lanes, onCardClick, onCardDrop]);

  return (
    <div className={`kanban-wrapper ${classes.kanbanWrapper}`}>
      <div className={`kanban-board ${classes.kanbanBoard}`}>
        {memoizedLanes}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Compare lanes array
  if (prevProps.lanes.length !== nextProps.lanes.length) return false;

  // Deep comparison of lanes
  return prevProps.lanes.every((lane, idx) => {
    const nextLane = nextProps.lanes[idx];
    if (!nextLane) return false;
    if (lane.id !== nextLane.id || lane.title !== nextLane.title) return false;
    if (lane.cards.length !== nextLane.cards.length) return false;
    return lane.cards.every((card, cardIdx) =>
      card.id === nextLane.cards[cardIdx]?.id
    );
  }) &&
  prevProps.onCardClick === nextProps.onCardClick &&
  prevProps.onCardDrop === nextProps.onCardDrop;
});

KanbanBoard.displayName = "KanbanBoard";

export default KanbanBoard;
